import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
	type QueryClient,
	type UseQueryOptions,
} from "@tanstack/react-query";
import { obterErroApi } from "@/utils/errors/apiError";
import { keys_permissoes } from "@/permissoes/hooks/usePermissoes";
import { PERMISSOES_RESTRICOES } from "@/permissoes/restricoes/restricoes.constants";
import type { CredenciaisEntrarJwt } from "@auth/services/jwt/tiposAutenticacaoJwt";
import { isSignedInteger } from "@/utils/utils";
import {
	atualizarTokenAuthSpace,
	autenticarLoginUnificadoSpace,
	verificarOtpLoginUnificadoSpace,
	autenticarSpace,
	criarGrupoUsuario,
	duplicarGrupoUsuario,
	editarGrupoUsuario,
	excluirGrupoUsuario,
	listarAuditoriaGrupo,
	listarAuditoriaRecurso,
	listarGruposUsuario,
	listarUsuariosDoGrupo,
	obterArvoreRestricoesGrupo,
	recuperarLoginPorId,
	recuperarLoginPorNome,
	salvarRestricoesGrupo,
} from "../control/authenticator.spaceApi";
import type {
	AuthLoginRequestDto,
	AuthLoginOtpRequestDto,
	IClonarGrupoUsuarioRequest,
	ICriarGrupoUsuarioRequest,
	IEditarGrupoUsuarioRequest,
	IExcluirGrupoUsuarioRequest,
	IListarAuditoriaGrupoRequest,
	IListarAuditoriaRecursoRequest,
	IListarGruposUsuarioRequest,
	IListarUsuariosGrupoRequest,
	IListarUsuariosGrupoResponse,
	ISalvarRestricoesGrupoRequest,
} from "../dtos/authenticator.dto";
import { MIN_CARACTERES_LOOKUP_USUARIO } from "@/configs/system";

// --------------------------------- TYPES ---------------------------------
type SolicitacaoAuthLogin<T> = {
	readonly payload: T;
	readonly signal: AbortSignal;
};

type OpcoesConsultaUsuariosGrupo = Pick<
	UseQueryOptions<IListarUsuariosGrupoResponse | null>,
	"staleTime" | "gcTime" | "placeholderData"
>;

// ------------------------------- CONSTANTS -------------------------------
export const keys_authenticator = {
	base: ["space"] as const,
	authenticator: () => [...keys_authenticator.base, "authenticator"] as const,
	lookup: () => [...keys_authenticator.authenticator(), "lookup"] as const,
	recuperarLoginPorNome: (nome: string, groupId: string) =>
		[...keys_authenticator.lookup(), "nome", groupId, nome.trim()] as const,
	recuperarLoginPorId: (id: string, groupId: string) =>
		[...keys_authenticator.lookup(), "id", groupId, id.trim()] as const,
	loginUnificado: () => [...keys_authenticator.authenticator(), "auth-login"] as const,
	loginOtp: () => [...keys_authenticator.authenticator(), "auth-login-otp"] as const,
	login: () => [...keys_authenticator.authenticator(), "login"] as const,
	refresh: () => [...keys_authenticator.authenticator(), "refresh"] as const,
	listarGruposUsuario: (req?: IListarGruposUsuarioRequest) =>
		[...keys_authenticator.authenticator(), "listar-grupos-usuario", ...(req ? [req] : [])] as const,
	arvoreRestricoes: (idCadGrupoUsuario: number) =>
		[...keys_authenticator.authenticator(), "arvore-restricoes", idCadGrupoUsuario] as const,
	usuariosGrupo: (req: IListarUsuariosGrupoRequest) =>
		[...keys_authenticator.authenticator(), "usuarios-grupo", req] as const,
	auditoriaGrupoBase: () => [...keys_authenticator.authenticator(), "auditoria-grupo"] as const,
	auditoriaGrupo: (req: IListarAuditoriaGrupoRequest) => [...keys_authenticator.auditoriaGrupoBase(), req] as const,
	auditoriaRecursoBase: () => [...keys_authenticator.authenticator(), "auditoria-recurso"] as const,
	auditoriaRecurso: (req: IListarAuditoriaRecursoRequest) =>
		[...keys_authenticator.auditoriaRecursoBase(), req] as const,
};

// --------------------------------------------- LOOKUP USUARIO -----------------------------------------------------
// -------------------------------- HELPERS --------------------------------
function revalidarPermissaoNegada(error: unknown, tag: string, queryClient: QueryClient) {
	if (obterErroApi(error)?.code !== "ACESSO_NEGADO") return;
	void queryClient.invalidateQueries({
		queryKey: keys_permissoes.verificaPermissao({ tipo: "USUARIO", tag_permissao: tag }),
	});
}

// -------------------------------- FUNCTIONS ------------------------------
export function useRecuperarLoginPorNome(nome: string, groupId: string, enabled = true) {
	const nomeNormalizado = nome.trim();

	return useQuery({
		queryKey: keys_authenticator.recuperarLoginPorNome(nomeNormalizado, groupId),
		queryFn: ({ signal }) => recuperarLoginPorNome(nomeNormalizado, groupId, signal),
		enabled: enabled && Boolean(groupId) && nomeNormalizado.length >= MIN_CARACTERES_LOOKUP_USUARIO,
		staleTime: 30_000,
		gcTime: 2 * 60_000,
		retry: 0,
	});
}

export function useRecuperarLoginPorId(id: string, groupId: string, enabled = true) {
	const idNormalizado = id.trim();
	const podeBuscar = idNormalizado.length > 0 && isSignedInteger(idNormalizado);

	return useQuery({
		queryKey: keys_authenticator.recuperarLoginPorId(idNormalizado, groupId),
		queryFn: ({ signal }) => recuperarLoginPorId(idNormalizado, groupId, signal),
		enabled: enabled && Boolean(groupId) && podeBuscar,
		staleTime: 30_000,
		gcTime: 2 * 60_000,
		retry: 0,
	});
}

// --------------------------------------------- AUTENTICACAO -----------------------------------------------------
export function useAutenticarLoginUnificadoSpace() {
	return useMutation({
		mutationKey: keys_authenticator.loginUnificado(),
		mutationFn: ({ payload, signal }: SolicitacaoAuthLogin<AuthLoginRequestDto>) =>
			autenticarLoginUnificadoSpace(payload, signal),
		retry: false,
		meta: { silenciarErro: true },
	});
}

export function useVerificarOtpLoginUnificadoSpace() {
	return useMutation({
		mutationKey: keys_authenticator.loginOtp(),
		mutationFn: ({ payload, signal }: SolicitacaoAuthLogin<AuthLoginOtpRequestDto>) =>
			verificarOtpLoginUnificadoSpace(payload, signal),
		retry: false,
		meta: { silenciarErro: true },
	});
}

export function useAutenticarSpace() {
	return useMutation({
		mutationKey: keys_authenticator.login(),
		mutationFn: (payload: CredenciaisEntrarJwt) => autenticarSpace(payload),
		retry: false,
	});
}

export function useRefreshTokenAuthSpace() {
	return useMutation({
		mutationKey: keys_authenticator.refresh(),
		mutationFn: (groupId: string) => atualizarTokenAuthSpace(groupId),
		retry: false,
	});
}

// --------------------------------------------- GRUPO USUARIO -----------------------------------------------------
export function useListarGruposUsuario(req: IListarGruposUsuarioRequest, enabled = true) {
	return useQuery({
		queryKey: keys_authenticator.listarGruposUsuario(req),
		queryFn: () => listarGruposUsuario(req),
		enabled,
		staleTime: 0, //Sem cache
		gcTime: 0,
		retry: false,
	});
}

// ------------------------------------- CADASTRO DE RESTRICOES (PERMISSOES POR GRUPO) --------------------------------
export function useArvoreRestricoesGrupo(idCadGrupoUsuario: number | null, enabled = true) {
	return useQuery({
		queryKey: keys_authenticator.arvoreRestricoes(idCadGrupoUsuario ?? 0),
		queryFn: () => {
			if (idCadGrupoUsuario == null) return null;
			return obterArvoreRestricoesGrupo(idCadGrupoUsuario);
		},
		enabled: enabled && idCadGrupoUsuario != null,
		// Mantém a árvore do grupo anterior visível enquanto a nova carrega, evitando o "piscar"
		placeholderData: keepPreviousData,
		staleTime: 0, //Sem cache
		gcTime: 0,
		retry: false,
	});
}

export function useListarUsuariosDoGrupo(
	req: IListarUsuariosGrupoRequest,
	enabled = true,
	opcoes?: OpcoesConsultaUsuariosGrupo,
) {
	return useQuery({
		queryKey: keys_authenticator.usuariosGrupo(req),
		queryFn: () => {
			const idCadGrupoUsuario = req.id_cad_grupo_usuario;
			if (idCadGrupoUsuario == null) return null;

			return listarUsuariosDoGrupo({ ...req, id_cad_grupo_usuario: idCadGrupoUsuario });
		},
		enabled: enabled && req.id_cad_grupo_usuario != null,
		placeholderData: keepPreviousData,
		staleTime: 10 * 60_000,
		gcTime: 10 * 60_000,
		retry: false,
		...opcoes,
	});
}

export function useListarAuditoriaGrupo(req: IListarAuditoriaGrupoRequest, enabled = true) {
	return useQuery({
		queryKey: keys_authenticator.auditoriaGrupo(req),
		queryFn: () => {
			if (req.id_cad_grupo_usuario == null) return null;
			return listarAuditoriaGrupo(req);
		},
		enabled: enabled && req.id_cad_grupo_usuario != null,
		placeholderData: keepPreviousData,
		staleTime: 2 * 60_000,
		gcTime: 5 * 60_000,
		retry: false,
	});
}

export function useListarAuditoriaRecurso(req: IListarAuditoriaRecursoRequest, enabled = true) {
	return useQuery({
		queryKey: keys_authenticator.auditoriaRecurso(req),
		queryFn: () => {
			if (!req.tag_recurso) return null;
			return listarAuditoriaRecurso(req);
		},
		enabled: enabled && Boolean(req.tag_recurso),
		placeholderData: keepPreviousData,
		staleTime: 2 * 60_000,
		gcTime: 5 * 60_000,
		retry: false,
	});
}

export function useSalvarRestricoesGrupo() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (req: ISalvarRestricoesGrupoRequest) => salvarRestricoesGrupo(req),
		onError: (error) => revalidarPermissaoNegada(error, PERMISSOES_RESTRICOES.salvar, queryClient),
		onSuccess: (_data, req) => {
			void queryClient.invalidateQueries({
				queryKey: keys_authenticator.arvoreRestricoes(req.id_cad_grupo_usuario),
			});
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.listarGruposUsuario() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaGrupoBase() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaRecursoBase() });
		},
		retry: false,
	});
}

export function useCriarGrupoUsuario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (req: ICriarGrupoUsuarioRequest) => criarGrupoUsuario(req),
		onError: (error) => revalidarPermissaoNegada(error, PERMISSOES_RESTRICOES.criar, queryClient),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.listarGruposUsuario() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaGrupoBase() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaRecursoBase() });
		},
		retry: false,
	});
}

export function useEditarGrupoUsuario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (req: IEditarGrupoUsuarioRequest) => editarGrupoUsuario(req),
		onError: (error) => revalidarPermissaoNegada(error, PERMISSOES_RESTRICOES.editar, queryClient),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.listarGruposUsuario() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaGrupoBase() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaRecursoBase() });
		},
		retry: false,
	});
}

export function useDuplicarGrupoUsuario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (req: IClonarGrupoUsuarioRequest) => duplicarGrupoUsuario(req),
		onError: (error) => revalidarPermissaoNegada(error, PERMISSOES_RESTRICOES.duplicar, queryClient),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.listarGruposUsuario() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaGrupoBase() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaRecursoBase() });
		},
		retry: false,
	});
}

export function useExcluirGrupoUsuario() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (req: IExcluirGrupoUsuarioRequest) => excluirGrupoUsuario(req),
		onError: (error) => revalidarPermissaoNegada(error, PERMISSOES_RESTRICOES.excluir, queryClient),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.listarGruposUsuario() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaGrupoBase() });
			void queryClient.invalidateQueries({ queryKey: keys_authenticator.auditoriaRecursoBase() });
		},
		retry: false,
	});
}
